import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';
import Alert from '../../components/blog/alert';
import PostBody from '../../components/blog/PostBody';
import PostHeader from '../../components/blog/PostHeader';
import PostTitle from '../../components/blog/PostTitle';
import SectionSeparator from '../../components/blog/SectionSeperator';
import { getAllPostsWithSlug, getPostAndMorePosts } from '../../lib/contentful';
import { TIDAL_URL, WHITE_LABEL_TWITTER } from '../../lib/white-label';
import ErrorPage from 'next/error';
import { NextSeo } from 'next-seo';

const Container = styled.div`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
`;

const PostLayout = ({ post, children, preview }) => (
  <>
    <NextSeo
      title={post.title}
      description={post.excerpt}
      canonical={`${TIDAL_URL}/blog/${post.slug}`}
    />
    <Head>
      <title>{post.title}</title>
      {post.coverImage.url ? (
        <meta
          name="og:image"
          property="og:image"
          content={post.coverImage.url}
        />
      ) : null}
      <meta name="twitter:site" content={WHITE_LABEL_TWITTER} />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={post.excerpt} />
    </Head>
    <div className="min-h-screen">
      <Alert preview={preview} />
      <main>{children}</main>
    </div>
  </>
);

export default function Post({ post, morePosts, preview }) {
  const router = useRouter();

  if (!router.isFallback && !post) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <PostLayout preview={preview} post={post}>
      <Container>
        {router.isFallback ? (
          <PostTitle>Loading…</PostTitle>
        ) : (
          <>
            <article>
              <Head>
                <title>{post.title}</title>
                <meta property="og:image" content={post?.coverImage?.url} />
                <meta property="og:description" content={post.excerpt} />
                <meta name="twitter:site" content={WHITE_LABEL_TWITTER} />
                <meta name="twitter:title" content={post.title} />
                <meta name="twitter:description" content={post.excerpt} />
                <meta name="twitter:image" content={post?.coverImage?.url} />
                <meta name="twitter:card" content="summary_large_image" />
              </Head>
              <PostHeader
                title={post.title}
                coverImage={post.coverImage}
                date={post.date}
                author={post.author}
                slug={post.slug}
              />
              <PostBody content={post.content} />
            </article>
            <SectionSeparator />
            {/* {morePosts && morePosts.length > 0 && (
              <MoreStories posts={morePosts} />
            )} */}
          </>
        )}
      </Container>
    </PostLayout>
  );
}

export async function getStaticProps({ params, preview = false }) {
  if (!params.slug) {
    return { notFound: true };
  }

  const data = await getPostAndMorePosts(params.slug, preview);

  if (!data.post) {
    return { notFound: true };
  }

  return {
    props: {
      preview,
      post: data?.post ?? null,
      morePosts: data?.morePosts ?? null,
    },
  };
}

export async function getStaticPaths() {
  const allPosts = await getAllPostsWithSlug();
  return {
    paths: allPosts?.map(({ slug }) => `/blog/${slug}`) ?? [],
    fallback: 'blocking',
  };
}
